import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Star as StarIcon } from '@mui/icons-material';
import { gradingAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import GradingScaleFormDialog from './forms/GradingScaleFormDialog';

const INITIAL = { scale_name: '', scale_code: '', description: '', is_default: false, is_active: true };

const GradingScales = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.GRADING, ACTIONS.DELETE);
  const [scales, setScales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingScale, setEditingScale] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(INITIAL);

  useEffect(() => { if (canRead) fetchScales(); }, [canRead]);

  const fetchScales = async () => {
    setLoading(true);
    try {
      const response = await gradingAPI.getGradingScales();
      setScales(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch grading scales', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingScale) {
        await gradingAPI.updateGradingScale(editingScale.id, formData);
        setSnackbar({ open: true, message: 'Grading scale updated successfully', severity: 'success' });
      } else {
        await gradingAPI.createGradingScale(formData);
        setSnackbar({ open: true, message: 'Grading scale created successfully', severity: 'success' });
      }
      fetchScales();
      resetForm();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (scale) => { setEditingScale(scale); setFormData(scale); setShowForm(true); };
  const resetForm = () => { setFormData(INITIAL); setEditingScale(null); setShowForm(false); };

  const confirmDeleteAction = async () => {
    try {
      await gradingAPI.deleteGradingScale(confirmDelete);
      setSnackbar({ open: true, message: 'Grading scale deleted successfully', severity: 'success' });
      fetchScales();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete grading scale', severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view grading scales.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Grading Scales"
        subtitle="Manage grading systems (UCE, UACE, PLE)"
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)} fullWidth={isMobile}>Add Grading Scale</Button>
        ] : []}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : scales.length === 0 ? (
            <EmptyRecordsState message="No grading scales found." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={scales}
              renderItem={(scale) => (
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={700}>{scale.scale_name}</Typography>
                    {scale.is_default && <StarIcon color="warning" fontSize="small" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">{scale.scale_code}</Typography>
                  <Typography variant="body2" color="text.secondary">{scale.description || 'No description'}</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip label={scale.is_active ? 'Active' : 'Inactive'} color={scale.is_active ? 'success' : 'default'} size="small" />
                    <Box>
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(scale)}><EditIcon /></IconButton>}
                      {canDelete && <IconButton size="small" color="error" onClick={() => setConfirmDelete(scale.id)}><DeleteIcon /></IconButton>}
                    </Box>
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Default</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scales.map((scale) => (
                    <TableRow key={scale.id} hover>
                      <TableCell><strong>{scale.scale_code}</strong></TableCell>
                      <TableCell>{scale.scale_name}</TableCell>
                      <TableCell>{scale.description}</TableCell>
                      <TableCell>{scale.is_default && <StarIcon color="warning" />}</TableCell>
                      <TableCell><Chip label={scale.is_active ? 'Active' : 'Inactive'} color={scale.is_active ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(scale)}><EditIcon /></IconButton>}
                        {canDelete && <IconButton size="small" color="error" onClick={() => setConfirmDelete(scale.id)}><DeleteIcon /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Grading Scale</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this grading scale? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <GradingScaleFormDialog open={showForm} onClose={resetForm} onSubmit={handleSubmit} editingScale={editingScale} formData={formData} setFormData={setFormData} saving={saving} isMobile={isMobile} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GradingScales;
